import React, { useState, useEffect } from 'react';
import API from '../api';
import { TrendingUp, TrendingDown, Check, X as XIcon, AlertCircle, RefreshCw } from 'lucide-react';

export default function ListinoPage() {
  const [proposte, setProposte] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/api/listino/proposte');
      setProposte(data);
    } catch (err) { console.error('Caricamento proposte listino fallito:', err); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const approva = async (id) => {
    await API.post(`/api/listino/proposte/${id}/approva`);
    load();
  };

  const rifiuta = async (id) => {
    await API.post(`/api/listino/proposte/${id}/rifiuta`);
    load();
  };

  const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
  const pendenti = proposte.filter(p => p.stato === 'proposta');
  const archiviate = proposte.filter(p => p.stato !== 'proposta');

  return (
    <div className="animate-fade-in" data-testid="listino-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight flex items-center gap-3">
            <RefreshCw size={28} className="text-primary" /> Listino Aziendale
          </h1>
          <p className="text-slate-500 mt-1">Proposte di aggiornamento prezzi basate sui preventivi validati</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <p className="text-xs font-bold text-slate-500 uppercase">Proposte Pendenti</p>
          <p className="text-2xl font-black text-primary">{pendenti.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <p className="text-xs font-bold text-slate-500 uppercase">Totale Proposte</p>
          <p className="text-2xl font-black">{proposte.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <p className="text-xs font-bold text-slate-500 uppercase">Archiviate</p>
          <p className="text-2xl font-black text-slate-400">{archiviate.length}</p>
        </div>
      </div>

      {/* Pending proposals */}
      {pendenti.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
            <AlertCircle size={18} className="text-yellow-600" /> Da Approvare
          </h2>
          <div className="space-y-3">
            {pendenti.map(p => (
              <div key={p.id} className="bg-white border border-yellow-200 rounded-md p-4" data-testid={`proposta-${p.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="font-bold text-sm">{p.descrizione}</p>
                    <p className="text-xs text-slate-400 font-mono mt-1">{p.codice_prezziario}</p>
                    <div className="flex items-center gap-6 mt-3">
                      <div>
                        <p className="text-xs text-slate-500">Prezzo Corrente</p>
                        <p className="font-mono font-bold text-sm">{fmt(p.prezzo_corrente_listino)}</p>
                      </div>
                      <div className="text-center">
                        {p.differenza_percentuale > 0 ? <TrendingUp size={20} className="text-red-500 mx-auto" /> : <TrendingDown size={20} className="text-green-500 mx-auto" />}
                        <p className={`text-xs font-bold ${p.differenza_percentuale > 0 ? 'text-red-500' : 'text-green-500'}`}>
                          {p.differenza_percentuale > 0 ? '+' : ''}{p.differenza_percentuale}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Prezzo Proposto</p>
                        <p className="font-mono font-bold text-sm text-primary">{fmt(p.prezzo_medio_calcolato)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Min / Max</p>
                        <p className="font-mono text-xs">{fmt(p.prezzo_min)} - {fmt(p.prezzo_max)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Casi</p>
                        <p className="font-bold text-sm">{p.numero_casi}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button onClick={() => approva(p.id)} data-testid={`approva-proposta-${p.id}`}
                      className="px-3 py-1.5 bg-accent-success text-white text-xs font-bold rounded-md hover:bg-green-700 transition-all flex items-center gap-1">
                      <Check size={14} /> Approva
                    </button>
                    <button onClick={() => rifiuta(p.id)} data-testid={`rifiuta-proposta-${p.id}`}
                      className="px-3 py-1.5 bg-accent-destructive text-white text-xs font-bold rounded-md hover:bg-red-700 transition-all flex items-center gap-1">
                      <XIcon size={14} /> Rifiuta
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Archived */}
      {archiviate.length > 0 && (
        <div>
          <h2 className="text-lg font-bold mb-3 text-slate-400">Storico</h2>
          <div className="space-y-2">
            {archiviate.map(p => (
              <div key={p.id} className="bg-white border border-slate-200 rounded-md p-3 flex items-center justify-between opacity-60">
                <div>
                  <p className="font-bold text-sm">{p.descrizione}</p>
                  <p className="text-xs text-slate-400">{p.codice_prezziario} - {fmt(p.prezzo_corrente_listino)} → {fmt(p.prezzo_medio_calcolato)}</p>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-bold ${p.stato === 'approvata' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {p.stato === 'approvata' ? 'Approvata' : 'Rifiutata'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {proposte.length === 0 && !loading && (
        <div className="text-center py-12 text-slate-400">
          <RefreshCw size={48} className="mx-auto mb-3 text-slate-300" />
          <p className="font-semibold">Nessuna proposta di aggiornamento</p>
          <p className="text-sm mt-1">Le proposte vengono generate automaticamente quando validi i prezzi sui preventivi</p>
        </div>
      )}
    </div>
  );
}
